/**
 * useShortcutCommandSearch — powers the "/" command search in the floating Ask
 * Heidi bar.
 *
 * Builds the entire searchable shortcut list (saved + all marketplace
 * shortcuts), filters it by what the user typed after "/", and tracks the
 * highlighted match. Because the floating input owns the editor + keydown, this
 * hook also wires the imperative handle the input uses to move the highlight
 * and run the highlighted result.
 */

import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from 'react';
import type { PinnedChip } from './evidence-settings-popover';
import type {
  ProtocolsButtonHandle,
  ShortcutSearchViewModel,
} from './protocols-button.types';
import type { ResolvedShortcut } from './shortcut-marketplace-data';
import {
  buildSearchableShortcuts,
  filterShortcuts,
  type SearchShortcut,
} from './shortcut-search';

interface UseShortcutCommandSearchArgs {
  /** Forwarded handle the floating input drives navigation + run through. */
  handleRef: Ref<ProtocolsButtonHandle>;
  pinnedChips: PinnedChip[];
  marketShortcuts: ResolvedShortcut[];
  /** Raw text after the leading "/". Empty means browse mode (no search). */
  searchQuery?: string;
  /** Run a result (click or Enter). */
  onRun: (shortcut: SearchShortcut) => void;
  /** Save a result from its inline bookmark. */
  onSave: (shortcut: SearchShortcut) => void;
  /** Unsave a result (undefined when removal isn't supported). */
  onUnsave?: (shortcut: SearchShortcut) => void;
}

export function useShortcutCommandSearch({
  handleRef,
  pinnedChips,
  marketShortcuts,
  searchQuery,
  onRun,
  onSave,
  onUnsave,
}: UseShortcutCommandSearchArgs): ShortcutSearchViewModel | undefined {
  const searchableShortcuts = useMemo(
    () => buildSearchableShortcuts(pinnedChips, marketShortcuts),
    [pinnedChips, marketShortcuts]
  );
  const trimmedQuery = (searchQuery ?? '').trim();
  const isSearching = trimmedQuery.length > 0;
  const results = useMemo(
    () => (isSearching ? filterShortcuts(searchableShortcuts, trimmedQuery) : []),
    [isSearching, searchableShortcuts, trimmedQuery]
  );

  const [highlightedIndex, setHighlightedIndex] = useState(0);
  // Restart at the top on each new query; clamp if results shrink.
  useEffect(() => setHighlightedIndex(0), [trimmedQuery]);
  useEffect(() => {
    setHighlightedIndex((i) =>
      results.length === 0 ? 0 : Math.min(i, results.length - 1)
    );
  }, [results.length]);

  // Refs keep the imperative handle stable while reading the latest values.
  const resultsRef = useRef(results);
  resultsRef.current = results;
  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;
  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;
  const trimmedQueryRef = useRef(trimmedQuery);
  trimmedQueryRef.current = trimmedQuery;

  // Fire "shortcut search used" once per search session — the first non-empty
  // query after the menu opens. Reset when the query clears (back to "/" browse
  // or menu close) so a fresh search re-emits.
  const searchUsedTrackedRef = useRef(false);
  useEffect(() => {
    if (!isSearching) {
      searchUsedTrackedRef.current = false;
      return;
    }
    if (searchUsedTrackedRef.current) return;
    searchUsedTrackedRef.current = true;
    track({
      event_name: TrackingEventName.EvidenceShortcutSearchUsed,
      props: {
        has_results: resultsRef.current.length > 0,
        results_count: resultsRef.current.length,
        // Never log raw query text — free-form "/" search is PHI-adjacent.
        query_length: trimmedQueryRef.current.length,
      },
      options: { amplitude: false },
    });
  }, [isSearching]);

  useImperativeHandle(
    handleRef,
    () => ({
      moveHighlight: (delta: number) =>
        setHighlightedIndex((i) => {
          const len = resultsRef.current.length;
          if (len === 0) return 0;
          return Math.min(len - 1, Math.max(0, i + delta));
        }),
      runHighlighted: () => {
        const selected = resultsRef.current[highlightedIndexRef.current];
        if (!selected) return false;
        track({
          event_name: TrackingEventName.EvidenceShortcutRun,
          props: {
            shortcut_source: 'search',
            via: 'keyboard',
            shortcut_id: selected.id,
            query_length: trimmedQueryRef.current.length,
          },
          options: { amplitude: false },
        });
        onRunRef.current(selected);
        return true;
      },
      hasResults: () => resultsRef.current.length > 0,
    }),
    []
  );

  const handleSelect = useCallback(
    (shortcut: SearchShortcut) => {
      track({
        event_name: TrackingEventName.EvidenceShortcutRun,
        props: {
          shortcut_source: 'search',
          via: 'click',
          shortcut_id: shortcut.id,
          query_length: trimmedQuery.length,
        },
        options: { amplitude: false },
      });
      onRun(shortcut);
    },
    [onRun, trimmedQuery]
  );

  if (!isSearching) return undefined;
  return {
    isActive: true,
    query: trimmedQuery,
    results,
    highlightedIndex,
    onSelect: handleSelect,
    onSave,
    onUnsave,
  };
}
